/**
 * HelpLanding — the pilot user's orientation surface.
 *
 * Bill 2026-09-11: "build out an actual Help Module for the pilot user to help
 * them familiarize themselves with the UI".
 *
 * SCOPE DISCIPLINE: everything described here is a surface that actually
 * exists and is wired for Phase 1 — Home, New Acct, Documents. Modules hidden
 * for the pilot (Lobby, Tasks, Approvals, Compliance, Metrics, Maint, IRA, CD,
 * SDB) are deliberately NOT documented. Help that describes features an FSR
 * cannot reach is worse than no help: it sends them looking for a door that
 * is not there and makes them distrust the parts that do work.
 *
 * Phase 1 product scope is Individual / Joint / Minor only.
 */
import { useState } from 'react';
import {
  BookOpen, Info, CreditCard, FileText, UserPlus, LifeBuoy, AlertTriangle, Compass,
} from '@retail-ops/panel-shell/fi';
import { TourLauncher } from '@retail-ops/guided-tour';
import { SystemAboutPanel } from '@/components/common/SystemAboutPanel';
import './HelpLanding.css';

type TabId = 'start' | 'tours' | 'tasks' | 'trouble' | 'about';

const TABS: ReadonlyArray<{ id: TabId; label: string; icon: typeof BookOpen }> = [
  { id: 'start',   label: 'Getting started', icon: BookOpen },
  { id: 'tours',   label: 'Guided tours',    icon: Compass },
  { id: 'tasks',   label: 'How do I…',       icon: UserPlus },
  { id: 'trouble', label: 'If something breaks', icon: LifeBuoy },
  { id: 'about',   label: 'About this app',  icon: Info },
];

export function HelpLanding(): JSX.Element {
  const [tab, setTab] = useState<TabId>('start');

  return (
    <div className="help-page">
      <header className="help-page__head">
        <h1 className="help-page__title">Help</h1>
        <p className="help-page__sub">
          A short orientation to the parts of the app that are live for the pilot.
        </p>
      </header>

      <nav className="help-tabs" data-tour="help-tabs" role="tablist" aria-label="Help sections">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === tab}
              className={`help-tab ${t.id === tab ? 'is-active' : ''}`.trim()}
              onClick={() => setTab(t.id)}
            >
              <Icon size={14} aria-hidden />
              {t.label}
            </button>
          );
        })}
      </nav>

      <div className="help-body" role="tabpanel">
        {tab === 'start' && <GettingStarted />}
        {tab === 'tours' && (
          <section className="help-card" data-tour="help-tours">
            <h2 className="help-card__title">Guided tours</h2>
            <p className="help-card__lede">
              Short walkthroughs that point at the real controls, one at a time. The tour for your role
              starts by itself the first time you open the app; take any of them again here.
            </p>
            <TourLauncher />
          </section>
        )}
        {tab === 'tasks' && <HowDoI />}
        {tab === 'trouble' && <Troubleshooting />}
        {tab === 'about' && (
          <section className="help-card">
            <h2 className="help-card__title">About this app</h2>
            <p className="help-card__lede">
              Your identity, branch, build, and the status of the services the app depends on.
              Screenshot this panel when you report a problem — it answers most of the first
              questions before anyone has to ask them.
            </p>
            <SystemAboutPanel />
          </section>
        )}
      </div>
    </div>
  );
}

function GettingStarted(): JSX.Element {
  return (
    <>
      <section className="help-card">
        <h2 className="help-card__title">What this app is for right now</h2>
        <p>
          During the pilot the app does one job end to end: <strong>open a deposit account</strong>{' '}
          for a personal customer, capture the documents, book it to Core Director, and hand the
          customer their debit card.
        </p>
        <p className="help-note">
          <AlertTriangle size={14} aria-hidden />
          <span>
            Pilot products are <strong>Individual, Joint, and Minor</strong> only. IRA, CD, Safe
            Deposit, business entities, and trusts are not part of this phase — keep using the
            legacy process for those.
          </span>
        </p>
      </section>

      <section className="help-card">
        <h2 className="help-card__title">Test mode and Training mode</h2>
        <p>
          A coloured strip across the top of the app tells you which mode you are in. If there is
          no strip, you are working live.
        </p>
        <dl className="help-deflist">
          <div>
            <dt>Test mode (purple)</dt>
            <dd>
              The real Core Director, but <strong>test customers only</strong>: last name{' '}
              <strong>Test</strong> with a tax ID starting <strong>666</strong>, or a CIF starting{' '}
              <strong>000</strong> for a test customer with no tax ID. Real customers are hidden from
              search and cannot be added, opened, changed, signed or killed. This is how the pilot
              group tests.
            </dd>
          </div>
          <div>
            <dt>Training mode</dt>
            <dd>
              Everything is simulated: nothing reaches Core Director, LexisNexis or a customer. Use
              it to learn the screens. A training cart cannot be committed.
            </dd>
          </div>
        </dl>
        <p className="help-note">
          <Info size={14} aria-hidden />
          <span>Your mode is set on your user account by an administrator. You cannot switch it yourself.</span>
        </p>
      </section>

      <section className="help-card">
        <h2 className="help-card__title">The three places you will work</h2>
        <dl className="help-deflist">
          <div>
            <dt>Home</dt>
            <dd>
              Your starting point. <strong>New Account</strong> and <strong>Find Customer</strong>{' '}
              are the two big buttons. Below them: your own numbers for the month, the carts you
              have in flight, and any online applications waiting to be claimed.
            </dd>
          </div>
          <div>
            <dt>New Acct</dt>
            <dd>
              The account-opening flow itself — people, products, ownership, compliance, review,
              signing, then commit. You can leave part-way through; the cart is saved and the{' '}
              <strong>Resume</strong> chip in the top bar brings you straight back to it.
            </dd>
          </div>
          <div>
            <dt>Documents</dt>
            <dd>
              Everything on file for your customers: IDs, signed PDFs, uploads, and notices.
              Three views — <strong>My Customers</strong>, <strong>By Cart</strong>, and the full{' '}
              <strong>Library</strong>.
            </dd>
          </div>
        </dl>
      </section>

      <section className="help-card">
        <h2 className="help-card__title">Things worth knowing on day one</h2>
        <ul className="help-list">
          <li>
            To look someone up, use <strong>Find Customer</strong> on Home — by name, SSN, or CIF.
          </li>
          <li>
            The <strong>branch selector</strong> in the top bar sets which branch your work books
            to. Check it before you start if you are covering another location.
          </li>
          <li>
            New here? <strong>Help → Guided tours</strong> walks you through the screens, pointing at
            each control in turn.
          </li>
          <li>
            A cart is saved as you go. Closing the tab does not lose it.
          </li>
          <li>
            If you also hold an Admin, Risk Management or SysOps role, click{' '}
            <strong>your initials</strong> in the top bar for links to those panels. They open in
            your browser, not in Teams.
          </li>
          <li>
            You will see fewer menu items than the finished product has. That is deliberate for
            the pilot — the hidden ones are not ready, and we would rather they were absent than
            half-working.
          </li>
        </ul>
      </section>
    </>
  );
}

function HowDoI(): JSX.Element {
  return (
    <>
      <section className="help-card">
        <h2 className="help-card__title">
          <UserPlus size={16} aria-hidden /> Open a new account for someone in the branch
        </h2>
        <ol className="help-steps">
          <li>Home → <strong>New Account</strong>.</li>
          <li>
            Add every person who will be on the account — owners, beneficiaries, signers,
            custodians. Search by SSN, name, or CIF first; existing customers should be found,
            not re-created.
          </li>
          <li>Pick the product, then set ownership and roles per product.</li>
          <li>Work the compliance step, then review.</li>
          <li>Sign, then commit. The commit books the account to Core Director.</li>
        </ol>
      </section>

      <section className="help-card">
        <h2 className="help-card__title">
          <UserPlus size={16} aria-hidden /> Board someone who applied online
        </h2>
        <p>
          Online applications land on Home under <strong>Unclaimed online carts</strong>. Claim one
          and it becomes your cart — from there it is the same flow as an in-branch opening, with
          the applicant&rsquo;s details already filled in. Check their work rather than retyping it.
        </p>
        <p className="help-note">
          <AlertTriangle size={14} aria-hidden />
          <span>
            Bringing in new applications from the old online system is <strong>paused</strong> until
            pilot testing of entities, products, disclosures and signing is complete. While it is
            paused the panel says so and the Import button is greyed out.
          </span>
        </p>
      </section>

      <section className="help-card">
        <h2 className="help-card__title">
          <CreditCard size={16} aria-hidden /> Print and activate a debit card
        </h2>
        <ol className="help-steps">
          <li>Order the card as part of the account opening.</li>
          <li>Print it at the Entrust station (Production Queue Client → your branch queue → Move To → Process).</li>
          <li>
            Come back to the app and activate. The app checks Core Director and will tell you if
            the card is not printed yet rather than letting you activate something that does not
            exist.
          </li>
        </ol>
        <p className="help-note">
          <AlertTriangle size={14} aria-hidden />
          <span>
            A minor&rsquo;s card has lower limits than an adult&rsquo;s. If you are told the card
            is in a status the app cannot act on, do not force it — ask System Operations.
          </span>
        </p>
      </section>

      <section className="help-card">
        <h2 className="help-card__title">
          <FileText size={16} aria-hidden /> Find a document
        </h2>
        <p>
          <strong>Documents → My Customers</strong> groups everything by customer.{' '}
          <strong>By Cart</strong> shows what is attached to an application in progress.{' '}
          <strong>Library</strong> searches the whole library by filename, CIF, document type, and
          date. Click any document for an inline preview.
        </p>
      </section>
    </>
  );
}

function Troubleshooting(): JSX.Element {
  return (
    <>
      <section className="help-card">
        <h2 className="help-card__title">Before you report it</h2>
        <ol className="help-steps">
          <li>
            Click the <strong>life-ring button in the bottom-left corner</strong> of any screen. It
            asks what you were doing, what you expected, and what happened - and it sends the
            technical detail (the page, your build, service status, recent activity and any errors)
            along with your answers, so nobody has to ask you to reproduce it.
          </li>
          <li>
            Have an idea, or want something to work differently? Pick{' '}
            <strong>Suggestion or change request</strong> in the same form. Those go to management
            for review first, not straight to the developers.
          </li>
          <li>
            If you would rather screenshot: <strong>Help → About this app</strong> shows your officer
            code, branch, the exact build, and whether Core Director, LexisNexis, USPS and the scanner
            are reachable.
          </li>
          <li>
            If the app showed a red error box, its <strong>Copy error</strong> button captures the
            exact message.
          </li>
        </ol>
        <p className="help-note">
          <Info size={14} aria-hidden />
          <span>
            The About panel usually answers the first three questions anyone would ask you, which
            is most of the delay in getting something fixed.
          </span>
        </p>
      </section>

      <section className="help-card">
        <h2 className="help-card__title">Common things, and what they mean</h2>
        <dl className="help-deflist">
          <div>
            <dt>Scanner Bridge: Offline</dt>
            <dd>
              The local scanning helper is not running on this machine. Everything else still
              works — you just cannot scan an ID from here until it is started.
            </dd>
          </div>
          <div>
            <dt>Core Director: Offline</dt>
            <dd>
              The app cannot reach the core. Do not re-submit repeatedly; report it, because
              nothing will book until it is back.
            </dd>
          </div>
          <div>
            <dt>Something says &ldquo;not checked&rdquo;</dt>
            <dd>
              We did not measure it, which is not the same as it being broken. It is not an outage
              on its own.
            </dd>
          </div>
          <div>
            <dt>A menu item you expected is missing</dt>
            <dd>
              Almost certainly hidden on purpose for the pilot. Ask before assuming it broke.
            </dd>
          </div>
        </dl>
      </section>
    </>
  );
}

export default HelpLanding;
